/* eslint-disable */
/**
 * Controls the disclaimer popup lifecycle, cookie persistence, and logged-in popup-count syncing with the server.
 */

// The disclaimer popup html is generated in template _DisclaimerPopup.cshtml
import Cookies from 'js-cookie';
import type { MemberProfile } from '@/features/member-accounts/memberProfile';

interface DisclaimerPopupOptions {
    defaultCookieName: string;
    loggedInCookieName: string;
    optanonCookieName: string;
    cookieExpire: number;
    popupVisibleClass: string;
    disclaimerPopupSelector: string;
    disclaimerAcceptBtnSelector: string;
    popupCountRequestUrl: string;
    popupLimit: number;
}

interface OptanonGlobal {
    OnConsentChanged: (callback: () => void) => void;
}

declare global {
    interface Window {
        DisclaimerPopup: DisclaimerPopup;
        Optanon?: OptanonGlobal;
    }
}

export class DisclaimerPopup {
    private opts: DisclaimerPopupOptions;
    private disclaimerPopup: HTMLElement | null = null;
    private disclaimerAcceptBtn: HTMLElement | null = null;
    private body: HTMLElement | null = null;
    private infoPopupCount: number = 0;

    constructor() {
        this.opts = {
            defaultCookieName: 'infopopup-count',
            loggedInCookieName: 'infopopup-count-logged-in',
            optanonCookieName: 'OptanonConsent',
            cookieExpire: 365 * 10,
            popupVisibleClass: 'disclaimer-open',
            disclaimerPopupSelector: '.DisclaimerPopup',
            disclaimerAcceptBtnSelector: '#disclaimer-accept-btn',
            popupCountRequestUrl: '/api/umbmember/setinfopopupcount',
            popupLimit: 3, // The number of times the popup should be shown
        };
    }

    init(): void {
        this.disclaimerPopup = document.querySelector(this.opts.disclaimerPopupSelector);

        if (!this.exists()) {
            return;
        }

        this.disclaimerAcceptBtn = document.querySelector(this.opts.disclaimerAcceptBtnSelector);
        this.body = document.body;

        this.setCurrentInfoPopupCount();
        this.setListeners();
    }

    exists(): boolean {
        return !!this.disclaimerPopup;
    }

    /**
     * Check how many times the popup has been shown
     */
    setCurrentInfoPopupCount(): void {
        const memberInfo: Partial<MemberProfile> = window.memberInfo || {};
        
        const cookieCount: number = this.getCookie() || 0;    
        const storedCount: number = memberInfo.infoPopupCount || this.getCookie(this.opts.loggedInCookieName) || 0;
        this.infoPopupCount = Math.min(this.opts.popupLimit, cookieCount + storedCount);
        
        if (memberInfo) {
            const storedCount: number = memberInfo.infoPopupCount || 0;

            // If the popup was shown to user when logged out
            if (cookieCount > 0 && storedCount < this.opts.popupLimit) {
                this.sendInfoPopupCount();
            }
            
            this.removeCookie(this.opts.defaultCookieName);
            this.setCookie(this.opts.loggedInCookieName);
        }
    }

    show(): void {
        if (!this.exists()) {
            return;
        }
        
        if (this.infoPopupCount >= this.opts.popupLimit) {
            return;
        }

        if (this.body?.classList.contains(this.opts.popupVisibleClass)) {
            return;
        }
        
        this.infoPopupCount += 1;

        this.body?.classList.add(this.opts.popupVisibleClass);
    }

    setListeners(): void {
        if (this.disclaimerAcceptBtn) {
            this.disclaimerAcceptBtn.addEventListener('click', (e: Event) => {
                e.preventDefault();
                
                this.infoPopupCount++;

                if (window.memberInfo) {
                    this.sendInfoPopupCount();
                    // Popup count moved to database
                    this.setCookie(this.opts.loggedInCookieName);
                } else {
                    // Save popup count until user logins
                    this.setCookie(this.opts.defaultCookieName);
                }
                
                this.body?.classList.remove(this.opts.popupVisibleClass);
            });
        }

        if (typeof window.Optanon !== 'undefined') {
            window.Optanon.OnConsentChanged(() => {
                if (!this.isCookiesAccepted()) {
                    this.removePopupCookies();
                }
            });
        }
    }

    /**
     * Store popup count on server to keep track between devices
     */
    sendInfoPopupCount(): void {
        if (window.memberInfo) {
            const requestUrl: string = `${this.opts.popupCountRequestUrl}?count=${this.infoPopupCount}`;

            fetch(requestUrl, {
                method: 'POST'
            });
        }
    }

    getCookie(cookieName?: string): number {
        const actualCookieName: string = this.getCookieName(cookieName);
        const cookieVal: string | undefined = Cookies.get(actualCookieName);

        return parseInt(cookieVal || '0') || 0;
    }
    
    removePopupCookies(): void {
        this.removeCookie(this.opts.defaultCookieName);
        this.removeCookie(this.opts.loggedInCookieName);
    }

    removeCookie(cookieName: string): void {
        const actualCookieName: string = this.getCookieName(cookieName);
        Cookies.remove(actualCookieName);
    }
    
    isCookiesAccepted(): boolean {
        // OneTrust
        const consentCookie: string | undefined = Cookies.get(this.opts.optanonCookieName);

        // 3:0 means the user did not accept Functional Cookies
        if (consentCookie && consentCookie.indexOf("3:0") !== -1) {
            return false;
        }
        
        return true;
    }

    setCookie(cookieName: string): void {
        if (!this.isCookiesAccepted()) {
            return;
        }
        
        const actualCookieName: string = this.getCookieName(cookieName);
        Cookies.set(actualCookieName, this.infoPopupCount.toString(), {
            expires: this.opts.cookieExpire
        });
    }

    getCookieName(cookieName?: string): string {
        return cookieName ? 
            cookieName : 
            this.opts.defaultCookieName;
    }
}

document.addEventListener('DOMContentLoaded', () => {
    window.DisclaimerPopup = new DisclaimerPopup();
    window.DisclaimerPopup.init();

    setTimeout(() => {
        window.DisclaimerPopup.show();
    }, 400);
});
